import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { useDropzone } from "react-dropzone";
import Webcam from "react-webcam";
import NoControlsVideo from "../../NoControlsVideo";
import { useOnboarding } from "../../../contexts/OnboardingContext";
import { showToast } from "../../Toast";
import logger from "../../../utils/logger";
import {
  PhotoLibraryIcon,
  CameraIcon,
  Sparkle,
  playClip,
  prefersReducedMotion,
} from "../../funnel/FunnelScreens";
import { StepHeading, ContinueButton, CtaRow, Item, LinkButton, SubView } from "../NOKit";

// The avatar step, laid out like the funnel's "pick their first friend":
// add a photo (gallery, or the camera) to be drawn as a Pixley character, or
// pick a ready-made buddy. There is no skip: with three buddies there is
// always a one-tap choice, and a skipped avatar left the account with no
// character for its first story. The animation runs in the BACKGROUND (iOS
// pattern): it kicks off when the photo is confirmed and the parent keeps
// answering steps; the Meet reveal waits on avatarPending only if it's still
// running by then.

// Preset buddies. `src` is what gets stored as the profile picture (an
// absolute URL, so every client can render it — unchanged); `still` is the
// funnel's picture of the same character, shown on the card and uploaded by
// completeOnboarding as the child's first character (avatarBuddy), exactly
// as the funnel keeps a preset friend; `clip` brings the card alive.
const BUDDIES = [
  {
    name: "Zippy",
    kind: "tiger",
    src: "/assets/zippy-character.png",
    still: "/assets/funnel-friends/zippy-icon.webp",
    clip: "/assets/funnel-friends/zippy-icon.mp4",
    loop: "/assets/funnel-friends/zippy-icon-loop.webp",
  },
  {
    name: "Benny",
    kind: "bunny",
    src: "/assets/benny.png",
    still: "/assets/funnel-friends/benny.webp",
    clip: "/assets/funnel-friends/benny.mp4",
    loop: "/assets/funnel-friends/benny-loop.webp",
  },
  { name: "Pip", kind: "panda", src: "/assets/pip.png", still: "/assets/pip.png" },
];

// Phones and tablets get the native camera sheet through a capture input;
// a desktop with a webcam gets the in-card camera.
const isTouchDevice = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(pointer: coarse)").matches || navigator.maxTouchPoints > 0);

const BURST = ["a", "b", "c", "d"];
const isHover = (e) => e.pointerType !== "touch";

// One buddy card (the funnel's FriendCard): the still, and the clip over it
// once it's playing — on hover, on tap, and kept looping while selected.
const BuddyCard = ({ buddy, selected, onTap }) => {
  const videoRef = useRef(null);
  const [alive, setAlive] = useState(false);
  const [burst, setBurst] = useState(0);

  const startClip = () => {
    if (buddy.clip && videoRef.current?.paused) playClip(videoRef.current);
  };
  const stopClip = () => {
    setAlive(false);
    const video = videoRef.current;
    if (video && !video.paused) video.pause();
  };

  useEffect(() => {
    if (!selected) stopClip();
  }, [selected]);

  const handleTap = () => {
    onTap(buddy);
    if (prefersReducedMotion()) return;
    setBurst((b) => b + 1);
    startClip();
  };

  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={`${buddy.name} the ${buddy.kind}`}
      className={`fs-friend${selected ? " is-selected" : ""}${alive ? " is-alive" : ""}`}
      onClick={handleTap}
      onPointerEnter={(e) => isHover(e) && startClip()}
      onPointerLeave={(e) => isHover(e) && !selected && stopClip()}
    >
      <span className="fs-friend-art">
        <img src={buddy.still} alt="" width="120" height="120" />
        {buddy.clip && (
          <NoControlsVideo
            ref={videoRef}
            className="fs-friend-clip"
            src={buddy.clip}
            animated={selected ? buddy.loop : undefined}
            muted
            loop
            preload="auto"
            onPlaying={() => setAlive(true)}
            aria-hidden="true"
          />
        )}
      </span>
      {selected && burst > 0 && (
        <span key={burst} className="fs-friend-burst" aria-hidden="true">
          {BURST.map((k) => (
            <Sparkle key={k} className={`fs-gold fs-burst-${k}`} />
          ))}
        </span>
      )}
      <span className="fs-friend-name">{buddy.name}</span>
    </button>
  );
};

const ProfilePictureStep = () => {
  const {
    nextStep,
    setProfilePicture,
    setOriginalProfileImage,
    setAvatarBuddy,
    profilePicture,
    username,
    startAvatarAnimation,
  } = useOnboarding();
  const existingAvatar = profilePicture && !profilePicture.startsWith("default:") ? profilePicture : null;
  // choose → camera (desktop only) → preview → (next step); or result, when
  // coming back to this step with an avatar already chosen
  const [mode, setMode] = useState(existingAvatar ? "result" : "choose");
  const [photo, setPhoto] = useState(null);
  const [buddy, setBuddy] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const webcamRef = useRef(null);
  const cameraInputRef = useRef(null);

  const name = username || "your child";

  const readPhoto = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setPhoto(reader.result);
      setMode("preview");
    };
    reader.readAsDataURL(file);
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) readPhoto(acceptedFiles[0]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [".png", ".jpg", ".jpeg", ".gif", ".webp", ".heic"] },
    maxFiles: 1,
    multiple: false,
  });

  const openCamera = () => {
    if (isTouchDevice() || !navigator.mediaDevices?.getUserMedia) {
      cameraInputRef.current?.click();
      return;
    }
    setCameraReady(false);
    setMode("camera");
  };

  const handleCapture = useCallback(() => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (!imageSrc) return;
    const img = new Image();
    img.onload = () => {
      // Mirror it, so the photo matches what they saw in the preview
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(img, 0, 0);
      setPhoto(canvas.toDataURL("image/jpeg"));
      setMode("preview");
    };
    img.src = imageSrc;
  }, []);

  const compressImage = async (imageDataUrl, maxWidth = 1024, quality = 0.8) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let width = img.width;
        let height = img.height;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => resolve(blob), "image/jpeg", quality);
      };
      img.src = imageDataUrl;
    });
  };

  // Kick the animation off and advance immediately — interests + discovery
  // give it 30-60s of natural cover.
  const handleAnimate = async (imageDataUrl) => {
    try {
      const compressedBlob = await compressImage(imageDataUrl, 1024, 0.8);
      const file = new File([compressedBlob], "photo.jpg", { type: "image/jpeg" });
      setOriginalProfileImage(imageDataUrl);
      setAvatarBuddy(null);
      startAvatarAnimation(file);
      nextStep();
    } catch (error) {
      logger.error("Error preparing image:", error);
      showToast("Couldn't read that photo. Please try another.", "error");
    }
  };

  const handlePickBuddy = (picked) => {
    setOriginalProfileImage(null);
    // The character is made from the same still the funnel uses for this friend
    setAvatarBuddy({ name: picked.name, src: picked.still });
    setProfilePicture(`https://www.pixleyai.com${picked.src}`);
    nextStep();
  };

  const handleRedoAvatar = () => {
    setPhoto(null);
    setBuddy(null);
    setProfilePicture(null);
    setOriginalProfileImage(null);
    setAvatarBuddy(null);
    setMode("choose");
  };

  return (
    <>
      {/* The camera sheet on phones (and the fallback when a webcam is refused) */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="user"
        onChange={(e) => {
          readPhoto(e.target.files?.[0]);
          e.target.value = "";
        }}
        style={{ display: "none" }}
      />

      <AnimatePresence mode="wait">
        {mode === "choose" && (
          <SubView key="choose">
            <StepHeading
              eyebrow="Their avatar"
              title={`Make ${name} the`}
              accent="hero"
              subtitle="Add a photo and we'll draw them as a Pixley character — or pick a ready-made buddy."
            />

            <Item className="fs-photo-options">
              <div
                {...getRootProps({
                  className: `fs-photo-option${isDragActive ? " is-active" : ""}`,
                  role: "button",
                  "aria-label": "Upload a photo",
                })}
              >
                <input {...getInputProps()} />
                <PhotoLibraryIcon size={34} />
                <strong>{isDragActive ? "Drop it here" : "Upload photo"}</strong>
              </div>
              <button type="button" className="fs-photo-option" onClick={openCamera}>
                <CameraIcon size={34} />
                <strong>Take photo</strong>
              </button>
            </Item>

            <Item className="fs-divider">
              <span>Or pick a Pixley buddy</span>
            </Item>

            <Item className="fs-friends" role="radiogroup" aria-label="Pixley buddies">
              {BUDDIES.map((b) => (
                <BuddyCard
                  key={b.name}
                  buddy={b}
                  selected={buddy?.name === b.name}
                  onTap={(picked) => setBuddy((cur) => (cur?.name === picked.name ? null : picked))}
                />
              ))}
            </Item>

            <CtaRow>
              <ContinueButton
                onClick={() => buddy && handlePickBuddy(buddy)}
                disabled={!buddy}
                label={buddy ? `Meet ${buddy.name}` : "Add a photo or pick a buddy"}
              />
            </CtaRow>
          </SubView>
        )}

        {mode === "camera" && (
          <SubView key="camera">
            <StepHeading eyebrow="Say cheese" title="Strike a" accent="pose!" subtitle="A clear, bright photo of their face works best." />
            <Item className="ob-camera">
              <Webcam
                ref={webcamRef}
                audio={false}
                screenshotFormat="image/jpeg"
                videoConstraints={{ facingMode: "user", width: { ideal: 1280 }, height: { ideal: 960 } }}
                onUserMedia={() => setCameraReady(true)}
                onUserMediaError={(error) => {
                  logger.warn("Camera unavailable, falling back to file picker:", error);
                  showToast("We couldn't open your camera — choose a photo instead.", "warning");
                  setMode("choose");
                }}
              />
            </Item>
            <Item className="ob-actions">
              <button type="button" className="ob-secondary" onClick={() => setMode("choose")}>
                Cancel
              </button>
              <ContinueButton onClick={handleCapture} disabled={!cameraReady} label="Take the photo" />
            </Item>
          </SubView>
        )}

        {mode === "preview" && (
          <SubView key="preview">
            <StepHeading eyebrow="Looking good" title="Ready to bring them" accent="to life?" />
            <Item className="ob-portrait">
              <img src={photo} alt="What you added" />
            </Item>
            <CtaRow>
              <ContinueButton onClick={() => handleAnimate(photo)} label="Bring them to life" />
            </CtaRow>
            <LinkButton
              onClick={() => {
                setPhoto(null);
                setMode("choose");
              }}
            >
              Try another photo
            </LinkButton>
          </SubView>
        )}

        {mode === "result" && (
          <SubView key="result">
            <StepHeading
              eyebrow="Their avatar"
              title="Looking"
              accent="great!"
              subtitle={`This is who ${name} will be in every story.`}
            />
            <Item className="ob-portrait">
              <img src={existingAvatar} alt={`${name}'s avatar`} />
            </Item>
            <CtaRow>
              <ContinueButton onClick={nextStep} />
            </CtaRow>
            <LinkButton onClick={handleRedoAvatar}>Choose differently</LinkButton>
          </SubView>
        )}
      </AnimatePresence>
    </>
  );
};

export default ProfilePictureStep;
